<script setup>
// Astra DS · DataRow — la fila tabular del sistema.
//
// Figma: Pattern/DataRow-sched 633:288 · Pattern/DataRow-track 634:293.
//
// Dos variantes de un mismo patrón, no dos componentes: cambian las columnas,
// no el comportamiento. En Figma son dos nodos porque una instancia no puede
// ganar ni perder hijos; en Vue es una prop.
//
//   sched  parrilla    hora · programa · género · residente · estado
//   track  cola e      hora · portada · artista · canción · álbum · programa
//          historial
//
// La tercera variante que documentaba DESIGN.md, `index`, ya no existe: el
// índice de Actualidad pasó a ser una rejilla de GridCell.
//
// 🔴 **`track` gana `tipo` (01.11.26)**, el conjunto `2718:5897` de Figma:
//
//   pista             la fila de siempre (`634:293`)
//   podcast           las canciones de un podcast plegadas (`2718:5874`): el
//                     artista con el chip PODCAST a 10, y en las columnas de
//                     canción y disco el resumen y la acción VER LAS N / OCULTAR
//   podcast-cancion   cada canción del podcast abierto (`2718:5888`): sin
//                     portada ni artista —los huecos quedan y las columnas
//                     alinean—, con «canción · disco [ año ]»
//
// Entrega `2026-11-01-radio-podcast-en-lo-que-sigue.md`. Solo escritorio.
import ChipPodcast from "@/components/ds/ChipPodcast.vue";
import MiniWave from "@/components/ds/MiniWave.vue";
import SignalDot from "@/components/ds/SignalDot.vue";
import { arte, esPlaceholder } from "@/services/placeholder";

const emit = defineEmits(["nombre"]);

defineProps({
  variant: {
    type: String,
    default: "sched",
    validator: (v) => ["sched", "track"].includes(v),
  },

  // Solo `track`. Ver arriba.
  tipo: {
    type: String,
    default: "pista",
    validator: (v) => ["pista", "podcast", "podcast-cancion"].includes(v),
  },
  // Solo `tipo="podcast"`: VER LAS N u OCULTAR, y si está abierta (para
  // `aria-expanded`). El resumen —«3 canciones ·», «1 de 3 · canción ·»— va en
  // `meta`.
  accion: { type: String, default: "" },
  abierto: { type: Boolean, default: false },

  // Primera columna. Franja horaria en `sched` («16–18»), hora en `track`.
  time: { type: String, default: "" },

  // La columna que crece. Nombre del programa o nombre del artista.
  name: { type: String, default: "" },

  // 🔴 **Si el nombre abre la ficha del artista** (24.09.26). Va como propiedad
  // y no por si alguien escucha el evento, porque en la variante `sched` el
  // nombre es un **programa** y ahí no hay ficha que abrir: quien lo pone sabe
  // qué está pintando.
  nombrePulsable: { type: Boolean, default: false },

  // Columna secundaria: género en `sched`, álbum en `track`.
  meta: { type: String, default: "" },

  // Solo `track`: el nombre de la canción, entre el artista y el álbum.
  // Encargo de Pablo (18.09.26): la fila decía quién suena y en qué disco, pero
  // no qué tema, que es el dato que se busca primero en una cola.
  track: { type: String, default: "" },

  // Solo `sched`. Se oculta con showResident, que en Figma es un booleano.
  resident: { type: String, default: "" },
  showResident: { type: Boolean, default: true },

  // Última columna, alineada a la derecha: estado o programa.
  tail: { type: String, default: "" },
  // La última columna en tinta de acento. Es lo que marca el tema en aire
  // dentro de una lista donde los demás están en cola: el rojo significa
  // directo, y aquí lo significa literalmente.
  tailAccent: { type: Boolean, default: false },

  // Solo `sched`. Rellena la fila y le pone la barra roja de 3px.
  live: { type: Boolean, default: false },
  // La portada en sus colores, sin el gris (Pablo, 27.10.26: en la parrilla de
  // Radio, «no apliques filtro a los colores de los álbumes»).
  color: { type: Boolean, default: false },

  // La onda al lado de la cola, en la fila que está en emisión. Es el booleano
  // de `DataRow · móvil`: sustituye al punto rojo y a la palabra LIVE. Donde el
  // navegador no entrega muestras —Safari— va el punto, como en el resto.
  ondas: { type: Boolean, default: false },
  levels: { type: Array, default: () => [] },
  playing: { type: Boolean, default: false },
  analisis: { type: String, default: "" },

  // Solo `track`.
  cover: { type: String, default: "" },
  coverAlt: { type: String, default: "" },

  // Estados. `loading` pinta el esqueleto de la fila; vacío y error no son de
  // la fila sino de la lista que la contiene.
  loading: { type: Boolean, default: false },
  interactive: { type: Boolean, default: false },
});
</script>

<template>
  <component
    :is="interactive ? 'button' : 'div'"
    class="data-row"
    :class="[
      `data-row--${variant}`,
      { 'is-live': live, 'is-loading': loading, 'is-interactive': interactive, 'is-color': color },
    ]"
    :type="interactive ? 'button' : null"
    :aria-busy="loading ? 'true' : null"
    :aria-expanded="tipo === 'podcast' && interactive ? String(abierto) : null"
  >
    <span v-if="live" class="data-row__bar" aria-hidden="true" />

    <span class="data-row__time">{{ loading ? "" : time }}</span>

    <span v-if="variant === 'track'" class="data-row__cover" :class="{ 'is-vacia': tipo === 'podcast-cancion' }">
      <img
        v-if="cover && !loading && tipo !== 'podcast-cancion'"
        :class="{ 'is-marca': esPlaceholder(cover) }"
        :src="arte(cover)"
        :alt="coverAlt"
        loading="lazy"
      />
    </span>

    <!-- Un botón y no un enlace: no lleva a ninguna parte, abre algo encima.
         Y solo cuando hay nombre: un botón vacío entra en el recorrido del
         teclado y no hace nada. -->
    <!-- La fila del podcast: el artista y, a 10, el chip. Toda la fila abre y
         cierra, así que el nombre aquí no es un botón (no cabe un botón dentro
         de otro). -->
    <span v-if="tipo === 'podcast'" class="data-row__artista">
      <span class="data-row__name">{{ name }}</span>
      <ChipPodcast />
    </span>
    <span v-else-if="tipo === 'podcast-cancion'" class="data-row__name" aria-hidden="true" />
    <button
      v-else-if="nombrePulsable && name && !loading"
      type="button"
      class="data-row__name data-row__name--pulsable"
      @click="emit('nombre', name)"
    >
      {{ name }}
    </button>
    <span v-else class="data-row__name">{{ loading ? "" : name }}</span>

    <!-- En las dos filas del podcast, canción y disco comparten columna: en
         Figma es una sola, «resumen» o «canción · disco [ año ]». -->
    <span v-if="tipo === 'podcast'" class="data-row__resumen">
      <span class="data-row__resumen-texto">{{ meta }}</span>
      <span class="data-row__accion">{{ accion }}</span>
    </span>
    <span v-else-if="tipo === 'podcast-cancion'" class="data-row__resumen">
      <span class="data-row__resumen-texto">{{ meta }}</span>
    </span>
    <template v-else>
      <span v-if="variant === 'track'" class="data-row__track">{{ loading ? "" : track }}</span>
      <span class="data-row__meta">{{ loading ? "" : meta }}</span>
    </template>

    <span
      v-if="variant === 'sched' && showResident"
      class="data-row__resident"
      >{{ loading ? "" : resident }}</span
    >

    <span class="data-row__tail" :class="{ 'is-accent': tailAccent, 'con-ondas': ondas && !loading }">
      {{ loading ? "" : tail }}
      <template v-if="ondas && !loading">
        <MiniWave v-if="analisis !== 'no'" class="data-row__ondas" :levels="levels" :playing="playing" />
        <SignalDot v-else class="data-row__ondas" :playing="playing" />
      </template>
    </span>
  </component>
</template>

<style scoped lang="scss">
@use "@/assets/scss/interaccion" as i;
@use "@/assets/scss/type" as t;

// Rejilla, no flex. Las columnas tienen que alinear entre filas: una cola de
// veinte temas con los álbumes desalineados deja de leerse como tabla.
//
// Las proporciones son las del artefacto —sched 1.6/1.4/1, track 1.5/1— pero en
// `fr` reales. En Figma son anchos fijos porque Figma no sabe repartir en
// proporción y reparte el sobrante a partes iguales; en CSS no hace falta
// mentir.
.data-row {
  position: relative;
  display: grid;
  align-items: center;
  gap: 16px;
  width: 100%;
  padding: 12px 28px;
  // Explícito y no heredado: hoy el border-box global lo pone el CSS de
  // Bootstrap, que se retira en C5. Sin esto, el día que salga, la altura fija
  // de abajo pasa a sumarse al padding y la fila mide 70px.
  box-sizing: border-box;
  margin: 0;
  border: 0;
  border-bottom: 1px solid var(--color-border-default);
  border-radius: 0;
  background: var(--color-bg-canvas);
  text-align: left;
  overflow: hidden;

  // Altura fija, la del artefacto. La última columna es una etiqueta corta que
  // a veces parte en dos líneas —«A continuación», «Canal Nocturno»— y en
  // Figma también lo hace; lo que no puede es empujar la fila. Una tabla con
  // filas de distinta altura deja de leerse como tabla.
  &--sched {
    height: 46px;
    grid-template-columns: 90px 1.6fr 1.4fr 1fr 92px;
  }

  &--track {
    height: 64px;
    grid-template-columns: 74px 40px 1.3fr 1.3fr 1fr 96px;
  }

  // Sin residente la fila tiene una columna menos, y la rejilla tiene que
  // saberlo o deja un hueco donde no hay nada.
  &--sched:not(:has(.data-row__resident)) {
    grid-template-columns: 90px 1.6fr 1.4fr 92px;
  }
}

// ── Estado en directo ──
// Fondo elevado y barra roja de 3px. La barra baja 1px por debajo para tapar
// la regla inferior: si no, la fila en antena aparece cortada por su propia
// línea.
.data-row.is-live {
  background: var(--color-bg-elevated);
}

.data-row__bar {
  position: absolute;
  top: 0;
  bottom: -1px;
  left: 0;
  width: 3px;
  background: var(--color-action-primary-default);
}

// ── Columnas ──

.data-row__time {
  @include t.display-row-time;
  color: var(--color-text-primary);
}

// En la cola la hora es un dato de bitácora, no un titular: baja a mono.
.data-row--track .data-row__time {
  @include t.mono-label;
  color: var(--color-text-secondary);
}

// El nombre pulsable: mismo texto, y al pasar por encima se marca en acento,
// que es lo que el sistema usa para decir «esto responde». Sin subrayado: no es
// un enlace, no lleva a otra página.
.data-row__name--pulsable {
  // 🔴 En bloque y no en flex (01.11.26): la regla global de `button` en
  // `astra-styles.scss` lo pone en flex centrado, y el nombre salía en medio de
  // su columna, no pegado a la izquierda como en Figma. En bloque, además, el
  // nombre largo vuelve a cortarse con puntos suspensivos.
  display: block;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
  text-align: left;

  &:hover,
  &:focus-visible {
    color: var(--color-text-accent);
  }

  &:focus-visible {
    outline: 2px solid var(--color-border-focus);
    outline-offset: 2px;
  }
}

.data-row__name {
  @include t.display-row-name;
  min-width: 0;
  color: var(--color-text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.data-row__track,
.data-row__meta,
.data-row__resident {
  @include t.mono-label;
  min-width: 0;
  color: var(--color-text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.data-row__tail {
  @include t.mono-micro;
  color: var(--color-text-tertiary);
  text-align: right;
  align-self: center;

  &.is-accent {
    color: var(--color-text-accent);
  }

  &.con-ondas {
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
  }
}

.data-row__ondas {
  flex: none;
  width: 14px;
  height: 9px;
}

// ── Las filas del podcast ──
// `2718:5874` y `2718:5888`. El resumen ocupa las columnas de canción y disco,
// para que la hora, la portada, el artista y el estado sigan alineados con las
// pistas de alrededor.
.data-row__artista {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  overflow: hidden;

  .data-row__name {
    flex: 0 1 auto;
  }
}

.data-row__resumen {
  @include t.mono-label;
  grid-column: span 2;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
}

.data-row__resumen-texto {
  min-width: 0;
  color: var(--color-text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
}

// La acción, como el enlace del crédito de la foto: primario y subrayado a 3.
.data-row__accion {
  flex: none;
  color: var(--color-text-primary);
  text-decoration: underline;
  text-underline-offset: 3px;
}

.data-row__cover.is-vacia {
  background: none;
}

// ── Portada ──
// Gris por defecto: una portada a color en un listado significa que eso está
// sonando ahora.
.data-row__cover {
  width: 40px;
  height: 40px;
  background: var(--color-art-mid);
  overflow: hidden;

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    // El filtro sale de la variable del patrón: la fila lo apaga entera. Ver
    // `scss/_interaccion.scss`.
    filter: var(--art-filtro, grayscale(1) contrast(1.15));
  }
}

.data-row.is-live .data-row__cover img,
.data-row.is-color .data-row__cover img {
  filter: none;
}

// Sin carátula, la A de la marca: no es una portada que vuelva a color, así
// que no entra en el gris. Ver `services/placeholder.js`.
.data-row__cover img.is-marca {
  filter: none;
}

// ── Interacción ──
// Sin sombra y sin desplazamiento: el hover rellena. Es la gramática del
// sistema, no una simplificación.
.data-row.is-interactive {
  // El arte recupera color con la fila entera, no solo con la portada.
  @include i.recupera-el-color;

  cursor: pointer;
  font: inherit;
  color: inherit;
  transition: background 0.12s linear;

  &:hover {
    background: var(--color-bg-elevated);
  }

  &:active {
    background: var(--color-bg-subtle);
  }

  &:focus-visible {
    outline: 2px solid var(--color-border-focus);
    outline-offset: -2px;
  }

  &:disabled {
    cursor: default;
    color: var(--color-text-disabled);

    .data-row__name,
    .data-row__time {
      color: var(--color-text-disabled);
    }
  }
}

// ── Cargando ──
// Bloques del color de la línea en cada columna. Sin brillo que recorre: el
// sistema no tiene gradientes y un esqueleto animado sería el único.
.data-row.is-loading {
  .data-row__time,
  .data-row__name,
  .data-row__track,
  .data-row__meta,
  .data-row__resident,
  .data-row__tail {
    min-height: 1em;
    background: var(--color-border-default);
  }

  .data-row__tail {
    justify-self: end;
    width: 100%;
  }
}

// ── Estrechado ──
// Las filas no reflowean, ocultan. Una fila partida en dos líneas deja de
// leerse como fila, así que caen primero las columnas secundarias.
@media (max-width: 1080px) {
  .data-row--sched .data-row__resident {
    display: none;
  }

  .data-row--sched {
    grid-template-columns: 90px 1.6fr 1.4fr 92px;
  }
}

@media (max-width: 880px) {
  .data-row {
    padding: 12px 20px;
    gap: 12px;
  }

  .data-row--sched {
    height: auto;
    min-height: 46px;
  }

  .data-row--track {
    height: auto;
    min-height: 64px;
  }

  .data-row__meta {
    display: none;
  }

  .data-row--sched,
  .data-row--sched:not(:has(.data-row__resident)) {
    grid-template-columns: 76px 1fr 92px;
  }

  .data-row--track {
    grid-template-columns: 62px 40px 1fr 1fr 96px;
  }
}
</style>
